import { toTitleCase } from 'utils/stringHelpers'
import { SELA_ACTIVE_STATUS, SELA_PROGRESS_STATUS } from './enums'
import {
  GRADE_BANDS,
  NO_DATA_PLACEHOLDER,
  ORDER_BY,
  TIER2_GRADE_BAND_KEYS,
  TIER2_DASHBOARD_SORT_KEYS,
  TIER2_LESSON_COMPLETION_SORT_KEYS,
} from './constants'
import pathwayNameMap from './pathwayNameMap'
import studentActivityTitleMap from './studentActivityTitleMap'

export const createChoiceCurriculumHeaders = (detail, createdHeaders) => {
  const meetings = detail.user_children

  meetings.forEach(meeting => {
    const newHeaderKey = `${meeting.display_title} - ${meeting.sub_content_type}`

    const newHeaderLabel =
      meeting.sub_content_type === 'leader-group'
        ? 'Attended Leader Team Meeting'
        : 'Attended Small-Group Meeting'

    const headerExists = createdHeaders.some(header => {
      return header.key === newHeaderKey
    })

    if (!headerExists)
      createdHeaders.push({ key: newHeaderKey, label: newHeaderLabel })
  })

  const certificateKey = 'Unit Certificate'
  const certificateHeaderLabel = 'Unit Certificate'
  const headerExists = createdHeaders.some(header => {
    return header.key === certificateKey
  })

  if (!headerExists)
    createdHeaders.push({
      key: certificateKey,
      label: certificateHeaderLabel,
    })
}

export const createModuleOrUnitHeaders = (
  createdHeaders,
  detail,
  detailType,
  index,
  newHeaderKey,
) => {
  let label

  if (detailType === 'moduleSela') {
    if (
      !newHeaderKey.includes('For School Principals') &&
      !newHeaderKey.includes('Meet With Your Leader Team') &&
      !newHeaderKey.includes('Getting Started') &&
      !newHeaderKey.includes('Kickoff')
    ) {
      label = `Unit ${index}`
    }
  }

  if (detailType === 'unitSela' && detail.type !== 'choiceCurriculum') {
    label = index + 1
  }
  const newHeaderLabel = label ? `${label}: ${newHeaderKey}` : newHeaderKey

  const headerExists = createdHeaders.some(header => {
    return header.key === newHeaderKey
  })

  if (!headerExists)
    createdHeaders.push({ key: newHeaderKey, label: newHeaderLabel })
}

export const createDataForMatchingDetail = (
  createdData,
  matchingDetail,
  user,
) => {
  const newData = {
    name: user.name,
    email: user.email,
    license_status: SELA_ACTIVE_STATUS[user.license_status],
    role: user.role,
    children_completed: matchingDetail.children_completed,
    total_children: matchingDetail.total_children,
  }

  const progress = !isNaN(matchingDetail.progress)
    ? `${Math.round(matchingDetail.progress * 100)}%`
    : NO_DATA_PLACEHOLDER

  assignModuleOrUnitData(matchingDetail, newData, progress)

  newData.progress = progress
  createdData.push(newData)
}

const assignModuleOrUnitData = (matchingDetail, newData, progress) => {
  matchingDetail?.user_children.forEach(child => {
    if (child.type === 'choiceCurriculum') {
      child.user_children?.forEach(meeting => {
        newData[`${meeting.display_title} - ${meeting.sub_content_type}`] =
          meeting.progress === 1 ? 'Y' : 'N'
      })
      newData['Unit Certificate'] = progress === '100%' ? 'Y' : 'N'
    } else {
      const unitOrLessonKey = child.display_title
      newData[unitOrLessonKey] =
        SELA_PROGRESS_STATUS[child.status] || NO_DATA_PLACEHOLDER
    }
  })
}

export const createDataForNoMatchingDetail = (createdData, headers, user) => {
  const newData = {
    name: user.name || NO_DATA_PLACEHOLDER,
    email: user.email,
    license_status: SELA_ACTIVE_STATUS[user.license_status],
    role: user.role,
  }

  headers.forEach(header => {
    const newHeader =
      header.key !== 'name' &&
      header.key !== 'email' &&
      header.key !== 'license_status' &&
      header.key !== 'role'

    if (newHeader) newData[header.key] = NO_DATA_PLACEHOLDER
  })

  createdData.push(newData)
}

export const compare = (a, b) => {
  if (a.license_status < b.license_status) return -1
  if (a.license_status > b.license_status) return 1

  if (a.name.toLowerCase() < b.name.toLowerCase()) return -1
  if (a.name.toLowerCase() > b.name.toLowerCase()) return 1

  return 0
}

export const sortGrades = grades => {
  return grades.sort((grade1, grade2) => {
    if (grade1.gradeName === 'Kindergarten') {
      return -1
    }
    if (grade2.gradeName === 'Kindergarten') {
      return 1
    }

    return grade1.gradeName.localeCompare(grade2.gradeName)
  })
}

export const getPathwayDates = pathwayPacingNodes => {
  if (!pathwayPacingNodes) return
  const formatDateOptions = {
    month: 'short',
    day: 'numeric',
  }

  const datesByPathway = {}

  const sortedPathwayPacingNodes = pathwayPacingNodes
    .filter(pacingNode => pacingNode && pacingNode.startDate)
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
    .map(({ endDate, nodeFriendlyName, startDate }) => {
      const zeroedEndDate = new Date(endDate).setHours(0, 0, 0, 0)
      const zeroedStartDate = new Date(startDate).setHours(0, 0, 0, 0)

      return {
        endDate: new Date(zeroedEndDate).toISOString(),
        startDate: new Date(zeroedStartDate).toISOString(),
        nodeFriendlyName,
      }
    })

  sortedPathwayPacingNodes.forEach(({ nodeFriendlyName, startDate }, index) => {
    const isPathwayStartNode = nodeFriendlyName.endsWith('collection 1')
    const pathwayName = pathwayNameMap[nodeFriendlyName]
    const pathwayStartDate = new Date(startDate)

    if (isPathwayStartNode) {
      const nextPathwayStartNode = sortedPathwayPacingNodes
        .slice(index + 1)
        .find(nextNode => nextNode.nodeFriendlyName.includes('collection 1'))

      if (nextPathwayStartNode) {
        const nextPathwayStartDate = new Date(nextPathwayStartNode.startDate)
        const pathwayEndDate = nextPathwayStartDate.setDate(
          nextPathwayStartDate.getDate() - 1,
        )
        const endDateTextFormatted = new Intl.DateTimeFormat(
          'en-US',
          formatDateOptions,
        ).format(new Date(pathwayEndDate))
        const startDateTextFormatted = new Intl.DateTimeFormat(
          'en-US',
          formatDateOptions,
        ).format(new Date(pathwayStartDate))

        datesByPathway[pathwayName] = {
          endDateInMs: pathwayEndDate,
          endDateTextFormatted,
          startDateInMs: pathwayStartDate.getTime(),
          startDateTextFormatted,
        }
      } else {
        const lastPathwayNode =
          sortedPathwayPacingNodes[sortedPathwayPacingNodes.length - 1]
        const daysToAdd = lastPathwayNode.nodeFriendlyName.endsWith(
          'collection 2',
        )
          ? 21
          : 14
        const lastPathwayNodeStartDate = new Date(lastPathwayNode.startDate)
        const pathwayEndDate = lastPathwayNodeStartDate.setDate(
          lastPathwayNodeStartDate.getDate() + daysToAdd,
        )

        const endDateTextFormatted = new Intl.DateTimeFormat(
          'en-US',
          formatDateOptions,
        ).format(new Date(pathwayEndDate))
        const startDateTextFormatted = new Intl.DateTimeFormat(
          'en-US',
          formatDateOptions,
        ).format(new Date(pathwayStartDate))

        datesByPathway[pathwayName] = {
          endDateInMs: pathwayEndDate,
          endDateTextFormatted,
          startDateInMs: pathwayStartDate.getTime(),
          startDateTextFormatted,
        }
      }
    }
  })

  return datesByPathway
}

export const getCurrentPacingEndDateAndName = sites => {
  if (!sites) return {}

  const formatDateOptions = {
    month: 'short',
    day: 'numeric',
  }
  let currentPacingName = 'School Setup Required'
  let currentPacingEndDate = ''
  let currentPacingEndDateFormatted = ''
  let currentPacingStartDate = ''
  let currentPacingStartDateFormatted = ''

  const sortedByDescEndStartHsPacing = sites
    .filter(collection => collection && collection.startDate)
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
    .map(collection => {
      const { startDate, endDate } = collection
      const zeroedEndDate = new Date(endDate).setHours(0, 0, 0, 0)
      const zeroedStartDate = new Date(startDate).setHours(0, 0, 0, 0)
      return {
        endDate: new Date(zeroedEndDate).toISOString(),
        startDate: new Date(zeroedStartDate).toISOString(),
        nodeFriendlyName: collection.nodeFriendlyName,
      }
    })

  const todaysDate = new Date(new Date().setHours(0, 0, 0, 0)).toISOString()

  if (todaysDate < sortedByDescEndStartHsPacing[0].startDate) {
    currentPacingName = 'Staff Kick-Off'
    const kickOffEndDate = new Date(sortedByDescEndStartHsPacing[0].startDate)
    kickOffEndDate.setDate(kickOffEndDate.getDate() - 1)
    const formattedKickOffEndDate = new Intl.DateTimeFormat(
      'en-US',
      formatDateOptions,
    ).format(kickOffEndDate)
    currentPacingEndDate = kickOffEndDate.toISOString()
    currentPacingEndDateFormatted = formattedKickOffEndDate
  } else if (
    todaysDate >= sortedByDescEndStartHsPacing[0].startDate &&
    todaysDate < sortedByDescEndStartHsPacing[1].startDate
  ) {
    currentPacingName =
      pathwayNameMap?.[sortedByDescEndStartHsPacing[0].nodeFriendlyName] || ''
    const trainingStartDate = new Date(
      sortedByDescEndStartHsPacing[0].startDate,
    )
    const trainingEndDate = new Date(sortedByDescEndStartHsPacing[1].startDate)
    trainingEndDate.setDate(trainingEndDate.getDate() - 1)
    const formattedTrainingEndDate = new Intl.DateTimeFormat(
      'en-US',
      formatDateOptions,
    ).format(trainingEndDate)
    const formattedTrainingStartDate = new Intl.DateTimeFormat(
      'en-US',
      formatDateOptions,
    ).format(trainingStartDate)
    currentPacingEndDate = trainingEndDate.toISOString()
    currentPacingEndDateFormatted = formattedTrainingEndDate
    currentPacingStartDate = trainingStartDate.toISOString()
    currentPacingStartDateFormatted = formattedTrainingStartDate
  } else {
    const lastCollection =
      sortedByDescEndStartHsPacing[sortedByDescEndStartHsPacing.length - 1]
    const currentCollection =
      sortedByDescEndStartHsPacing.find((collection, index) => {
        const nextCollection = sortedByDescEndStartHsPacing[index + 1]
        if (!nextCollection) {
          return true
        } else {
          return todaysDate < nextCollection?.startDate
        }
      }) || lastCollection

    const nextPathwayCollection =
      sortedByDescEndStartHsPacing
        .slice(sortedByDescEndStartHsPacing.indexOf(currentCollection))
        .find(collection => {
          if (currentCollection.nodeFriendlyName === 'hs program training') {
            return collection.nodeFriendlyName === 'hs b&c collection 1'
          } else {
            return !collection.nodeFriendlyName.includes(
              currentCollection.nodeFriendlyName.slice(0, -1),
            )
          }
        }) || currentCollection

    const currentPathway =
      pathwayNameMap?.[currentCollection.nodeFriendlyName] || ''
    currentPacingName = currentPathway
    const pathwayStartDate = new Date(currentCollection.startDate)
    const pathwayEndDate = new Date(nextPathwayCollection.startDate)

    if (currentCollection === lastCollection) {
      if (currentCollection.nodeFriendlyName.endsWith('collection 3')) {
        pathwayEndDate.setDate(pathwayEndDate.getDate() + 14)
      }
      if (currentCollection.nodeFriendlyName.endsWith('collection 2')) {
        pathwayEndDate.setDate(pathwayEndDate.getDate() + 21)
      }
    } else {
      pathwayEndDate.setDate(pathwayEndDate.getDate() - 1)
    }

    const formattedPathwayStartDate = new Intl.DateTimeFormat(
      'en-US',
      formatDateOptions,
    ).format(pathwayStartDate)
    const formattedPathwayEndDate = new Intl.DateTimeFormat(
      'en-US',
      formatDateOptions,
    ).format(pathwayEndDate)
    currentPacingStartDate = pathwayStartDate.toISOString()
    currentPacingEndDate = pathwayEndDate.toISOString()
    currentPacingEndDateFormatted = formattedPathwayEndDate
    currentPacingStartDateFormatted = formattedPathwayStartDate
  }

  return {
    currentPacingEndDate,
    currentPacingEndDateFormatted,
    currentPacingName,
    currentPacingStartDate,
    currentPacingStartDateFormatted,
  }
}

export const formatPathwayDataForTableByPracticeType = (
  pathways,
  practiceType,
) => {
  const isStudentActivity = practiceType === 'activity'
  return pathways.map(pathway => {
    return {
      completed: isStudentActivity
        ? pathway.number_student_activities_completed
        : pathway.collection_completed,
      pathwayNumber: pathway.pathway_number,
      total: isStudentActivity
        ? pathway.total_completed
        : pathway.collection_count,
    }
  })
}

export const createPracticeRowData = (data, practiceType, collectionCount) => {
  const formattedData = [
    {
      title: studentActivityTitleMap[`pathway${data.pathway_number}`]?.title,
      type: 'title',
    },
  ]

  for (let i = 1; i <= collectionCount; i++) {
    const collectionString = `collection_${i}`
    const rowData = {
      completedDate: data[`${collectionString}_marked_done_date`],
      progress_marked: data[`${collectionString}_raw_status`],
      status: data[`${collectionString}_status`],
      title:
        pathwayNameMap.titlesByPracticeType?.[practiceType]?.[
          `pathway${data.pathway_number}`
        ]?.[collectionString],
      type: 'data',
    }
    formattedData.push(rowData)
  }

  return formattedData
}

export const createStudentActivityRowData = data => {
  // this assumes 'data' is shaped like: {
  //   pathway_number: 1,
  //   collection_1_grade_9_status: 'completed',
  //   collection_1_grade_9_marked_done_date: '2023-09-01',
  // }
  const formattedData = [
    {
      title: studentActivityTitleMap[`pathway${data.pathway_number}`]?.title,
      type: 'title',
    },
  ]
  for (let key in data) {
    if (key.includes('_status')) {
      const collectionAndGradeName = key.split('_status')[0]
      const title =
        studentActivityTitleMap[`pathway${data.pathway_number}`]?.[
          collectionAndGradeName
        ]
      const collectionAndGradeNumbers = key.match(
        /collection_(\d+)_grade_(\d+)/,
      )
      const collectionNumber = parseInt(collectionAndGradeNumbers[1])
      const gradeNumber = parseInt(collectionAndGradeNumbers[2])
      const rowData = {
        completedDate: data[key.replace('_status', '_marked_done_date')],
        status: toTitleCase(data[key]),
        title,
        type: 'data',
      }
      const subTitleRowData = {
        title: `Collection ${collectionNumber}`,
        type: 'sub-title',
      }

      if (collectionAndGradeNumbers) {
        rowData.grade = `Grade ${gradeNumber}`

        if (gradeNumber === 9) {
          formattedData.push(subTitleRowData)
        }
      }
      formattedData.push(rowData)
    }
  }

  return formattedData
}

const createEmptyActivityPathway = (pathwayNumber, userId) => ({
  collection_1_grade_9_marked_done_date: null,
  collection_1_grade_9_status: '',
  collection_1_grade_10_marked_done_date: null,
  collection_1_grade_10_status: '',
  collection_1_grade_11_marked_done_date: null,
  collection_1_grade_11_status: '',
  collection_1_grade_12_marked_done_date: null,
  collection_1_grade_12_status: '',
  collection_2_grade_9_marked_done_date: null,
  collection_2_grade_9_status: '',
  collection_2_grade_10_marked_done_date: null,
  collection_2_grade_10_status: '',
  collection_2_grade_11_marked_done_date: null,
  collection_2_grade_11_status: '',
  collection_2_grade_12_marked_done_date: null,
  collection_2_grade_12_status: '',
  collection_3_grade_9_marked_done_date: null,
  collection_3_grade_9_status: '',
  collection_3_grade_10_marked_done_date: null,
  collection_3_grade_10_status: '',
  collection_3_grade_11_marked_done_date: null,
  collection_3_grade_11_status: '',
  collection_3_grade_12_marked_done_date: null,
  collection_3_grade_12_status: '',
  number_student_activities_completed: 0,
  pathway_number: pathwayNumber,
  ssapi_user_id: userId,
})

const createEmptyPracticesPathway = (
  pathwayNumber,
  userId,
  collectionCount,
) => {
  const emptyPracticesPathway = {
    collection_1_marked_done_date: null,
    collection_1_raw_status: '',
    collection_1_status: '',
    collection_2_marked_done_date: null,
    collection_2_raw_status: '',
    collection_2_status: '',
    collection_completed: 0,
    collection_count: collectionCount,
    pathway_number: pathwayNumber,
    ssapi_user_id: userId,
  }

  if (collectionCount === 3) {
    emptyPracticesPathway.collection_3_marked_done_date = null
    emptyPracticesPathway.collection_3_raw_status = ''
    emptyPracticesPathway.collection_3_status = ''
  }

  return emptyPracticesPathway
}

export const addMissingPathways = (
  pathways,
  numberOfPathways,
  collectionCount,
  practiceType,
  userId,
) => {
  const createEmptyPathway =
    practiceType === 'activity'
      ? createEmptyActivityPathway
      : createEmptyPracticesPathway
  const fullPathways = pathways
  const existingPathwayNumbers = pathways
    .filter(({ pathway_number }) => !!pathway_number)
    .map(({ pathway_number }) => pathway_number)

  for (let i = 1; i <= numberOfPathways; i++) {
    const pathwayNumberExists = existingPathwayNumbers.find(
      pathwayNumber => pathwayNumber === i,
    )
    if (!pathwayNumberExists) {
      fullPathways.push(createEmptyPathway(i, userId, collectionCount))
    }
  }

  return fullPathways
}

/**
 * Sorts and paginates data array using provided sort configuration
 * @param {Object} params - Configuration object
 * @param {Array} params.data - Array of items to sort/paginate (already transformed)
 * @param {Object} params.sortConfig - Object mapping sort keys to sort functions: { [sortKey]: (a, b, order) => number }
 * @param {string} params.sortKey - Current sort key
 * @param {string} params.orderBy - Sort direction ('asc' or 'desc')
 * @param {number} params.currentPage - Current page number (1-indexed)
 * @param {number} [params.pageSize=25] - Number of items per page
 * @returns {Array} Paginated, sorted array
 */
export const sortAndPaginateData = ({
  data,
  sortConfig,
  sortKey,
  orderBy,
  currentPage,
  pageSize = 25,
}) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return []
  }

  // Clone data array to avoid mutating original
  const sortedData = [...data]

  // Get sort function from config
  const sortFn = sortConfig[sortKey]
  if (!sortFn) {
    // If no sort function found, return data as-is (paginated)
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return sortedData.slice(startIndex, endIndex)
  }

  // Calculate sort order multiplier
  const order = orderBy === ORDER_BY.ASC ? 1 : -1

  // Sort data using the sort function
  sortedData.sort((a, b) => sortFn(a, b, order))

  // Calculate pagination
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize

  return sortedData.slice(startIndex, endIndex)
}

/**
 * Creates a numeric sort function
 * @param {Function} getValueFn - Function to extract numeric value from item
 * @param {number} [fallback=0] - Fallback value if getValueFn returns null/undefined
 * @returns {Function} Sort function that takes (a, b, order) and returns comparison result
 */
export const createNumericSort = (getValueFn, fallback = 0) => {
  return (a, b, order) => {
    const aValue = getValueFn(a) ?? fallback
    const bValue = getValueFn(b) ?? fallback
    return order * (aValue - bValue)
  }
}

/**
 * Creates a string sort function
 * @param {Function} getValueFn - Function to extract string value from item
 * @param {string} [fallback=''] - Fallback value if getValueFn returns null/undefined
 * @returns {Function} Sort function that takes (a, b, order) and returns comparison result
 */
export const createStringSort = (getValueFn, fallback = '') => {
  return (a, b, order) => {
    const aValue = getValueFn(a) ?? fallback
    const bValue = getValueFn(b) ?? fallback
    return order * aValue.localeCompare(bValue)
  }
}

/**
 * Creates a compound sort function that chains multiple sort functions
 * Returns the first non-zero comparison result, or 0 if all comparisons are equal
 * @param {...Function} sortFunctions - Array of sort functions to chain
 * @returns {Function} Sort function that takes (a, b, order) and returns comparison result
 */
export const createCompoundSort = (...sortFunctions) => {
  return (a, b, order) => {
    for (const sortFn of sortFunctions) {
      const result = sortFn(a, b, order)
      if (result !== 0) {
        return result
      }
    }
    return 0
  }
}

/**
 * Sort configuration for Tier2 Dashboard data
 * Maps sort keys to sort functions that take (a, b, order) and return comparison result
 */
export const TIER2_DASHBOARD_SORT_CONFIG = {
  [TIER2_DASHBOARD_SORT_KEYS.SCHOOLS]: createStringSort(
    item => item.schoolName || item.name,
    '',
  ),
  [TIER2_DASHBOARD_SORT_KEYS.SMALL_GROUPS_CREATED]: createNumericSort(
    item => item.smallGroupsCreated || item.classesCreated,
    0,
  ),
  [TIER2_DASHBOARD_SORT_KEYS.LESSONS_COMPLETED]: createNumericSort(
    item => item.lessonsCompleted,
    0,
  ),
  [TIER2_DASHBOARD_SORT_KEYS.K_2]: createNumericSort(
    item => item.gradeBandCounts?.[TIER2_GRADE_BAND_KEYS.K_2],
    0,
  ),
  [TIER2_DASHBOARD_SORT_KEYS.THREE_5]: createNumericSort(
    item => item.gradeBandCounts?.[TIER2_GRADE_BAND_KEYS.THREE_5],
    0,
  ),
  [TIER2_DASHBOARD_SORT_KEYS.SIX_8]: createNumericSort(
    item => item.gradeBandCounts?.[TIER2_GRADE_BAND_KEYS.SIX_8],
    0,
  ),
}

/**
 * Sort configuration for Tier2 Lesson Completion data
 * Maps sort keys to sort functions that take (a, b, order) and return comparison result
 */
export const TIER2_LESSON_COMPLETION_SORT_CONFIG = {
  [TIER2_LESSON_COMPLETION_SORT_KEYS.SMALL_GROUPS]: createCompoundSort(
    createStringSort(item => item.teacherFirstName, ''),
    createStringSort(item => item.groupName || item.className, ''),
  ),
  [TIER2_LESSON_COMPLETION_SORT_KEYS.STATUS]: createStringSort(
    item => item.status,
    '',
  ),
  [TIER2_LESSON_COMPLETION_SORT_KEYS.LESSONS_DONE]: createNumericSort(
    item => item.lessonsDone,
    0,
  ),
  [TIER2_LESSON_COMPLETION_SORT_KEYS.FIRST_UNIT]: createNumericSort(
    item => item.units?.[0]?.lessonsDone,
    0,
  ),
  [TIER2_LESSON_COMPLETION_SORT_KEYS.SECOND_UNIT]: createNumericSort(
    item => item.units?.[1]?.lessonsDone,
    0,
  ),
  [TIER2_LESSON_COMPLETION_SORT_KEYS.THIRD_UNIT]: createNumericSort(
    item => item.units?.[2]?.lessonsDone,
    0,
  ),
  [TIER2_LESSON_COMPLETION_SORT_KEYS.LAST_LOGGED_IN]: createNumericSort(
    item => (item.lastLoggedIn ? new Date(item.lastLoggedIn).getTime() : 0),
    0,
  ),
}

/**
 * Gets the display label for a Tier 2 grade band (e.g., "Grades K-2")
 * @param {string} gradeBand - The grade band URL key (e.g., "k-2", "3-5", "6-8")
 * @returns {string} The display label with "Grades" prefix, or fallback format
 */
export const getGradeBandLabel = gradeBand => {
  return (
    GRADE_BANDS.find(band => band.url === gradeBand)?.text ||
    `Grades ${gradeBand}`
  )
}
